import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import store from '../Store';

const DashboardPage = observer(() => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCategories: 0,
    totalPackages: 0,
    activeUsers: 0
  });

  const [recentActivities, setRecentActivities] = useState<any[]>([]);

  useEffect(() => {
    // Fetch all data for dashboard - مرة واحدة فقط
    const fetchDashboardData = async () => {
      try {
        console.log('Dashboard: Starting to fetch data...', new Date().toISOString());
        console.log('Dashboard: Store isAuthenticated:', store.isAuthenticated);
        console.log('Dashboard: Store token:', store.token ? 'exists' : 'missing');
        
        // جلب البيانات مرة واحدة فقط
        if (store.users.length === 0) {
          await store.fetchUsers();
        }
        if (store.categories.length === 0) {
          await store.fetchCategories();
        }
        if (store.packages.length === 0) {
          await store.fetchPackages();
        }

        // Calculate stats
        console.log('Dashboard: Calculating stats...');
        console.log('Dashboard: Users data:', store.users);
        console.log('Dashboard: Users length:', store.users.length);
        console.log('Dashboard: Categories data:', store.categories);
        console.log('Dashboard: Categories length:', store.categories.length);
        console.log('Dashboard: Packages data:', store.packages);
        console.log('Dashboard: Packages length:', store.packages.length);
        
        // استخدام toJS() لتحويل MobX observable إلى array عادي
        const usersArray = store.users.slice();
        const categoriesArray = store.categories.slice();
        const packagesArray = store.packages.slice();
        
        console.log('Dashboard: Users array:', usersArray);
        console.log('Dashboard: Categories array:', categoriesArray);
        console.log('Dashboard: Packages array:', packagesArray);
        
        const newStats = {
          totalUsers: usersArray.length,
          totalCategories: categoriesArray.length,
          totalPackages: packagesArray.length,
          activeUsers: usersArray.filter(user => user.account_status === 'active').length
        };
        
        console.log('Dashboard: Calculated stats:', newStats);
        setStats(newStats);

        // Generate recent activities from real data
        const activities = [];
        
        // Recent users
        const sortedUsers = [...usersArray].sort((a, b) => new Date(b.created_at || b.id).getTime() - new Date(a.created_at || a.id).getTime());
        const recentUsers = sortedUsers.slice(0, 3);
        
        for (const user of recentUsers) {
          activities.push({
            title: `مستخدم جديد: ${user.first_name} ${user.last_name}`,
            description: `تم تسجيل مستخدم جديد`,
            time: new Date(user.created_at || Date.now()).toLocaleDateString('en-US'),
            type: 'user' as const,
            user: user
          });
        }
        
        
        const sortedActivities = [...activities].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
        setRecentActivities(sortedActivities.slice(0, 6));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  type TrendType = 'up' | 'down' | 'neutral';

  const getTrendColor = (trend: TrendType) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend: TrendType) => {
    switch (trend) {
      case 'up': return '↗';
      case 'down': return '↘';
      default: return '→';
    }
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue }: { 
    title: string; 
    value: number; 
    icon: string; 
    color: string;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: number;
  }) => (
    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-300">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center shadow-lg`}>
              <span className="text-white text-xl">{icon}</span>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {title}
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {value.toLocaleString()}
              </p>
            </div>
          </div>
          {trend && trendValue && (
            <div className={`flex items-center text-sm ${getTrendColor(trend)}`}>
              <span className="mr-1">{getTrendIcon(trend)}</span>
              {trendValue}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const RecentActivityItem = ({ title, description, time, type, user }: { 
    title: string; 
    description: string; 
    time: string; 
    type: 'user' | 'category' | 'package';
    user?: any;
  }) => {
    const getTypeIcon = () => {
      switch (type) {
        case 'user': return '👤';
        case 'category': return '📁';
        case 'package': return '📦';
        default: return '📄';
      }
    };

    const getTypeColor = () => {
      switch (type) {
        case 'user': return 'bg-blue-500';
        case 'category': return 'bg-purple-500';
        case 'package': return 'bg-yellow-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="flex items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
        <div className={`flex-shrink-0 w-10 h-10 ${getTypeColor()} rounded-full flex items-center justify-center text-white shadow-lg`}>
          <span className="text-lg">{getTypeIcon()}</span>
        </div>
        <div className="flex-1 min-w-0 mr-4">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {title}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
            {description}
          </p>
          {user && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              البريد الإلكتروني: {user.email}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {time}
        </div>
      </div>
    );
  };

  if (store.isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 dark:text-gray-400">جاري تحميل البيانات...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">لوحة التحكم</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">مرحباً بك في نظام إدارة Spinndo</p>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              آخر تحديث: {new Date().toLocaleString('en-US')}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard
            title="إجمالي المستخدمين"
            value={stats.totalUsers}
            icon="👥"
            color="bg-gradient-to-r from-blue-500 to-blue-600"
            trend="up"
            trendValue={12}
          />
          <StatCard
            title="المستخدمين النشطين"
            value={stats.activeUsers}
            icon="✅"
            color="bg-gradient-to-r from-green-500 to-green-600"
            trend="up"
            trendValue={8}
          />
          <StatCard
            title="إجمالي الفئات"
            value={stats.totalCategories}
            icon="📁"
            color="bg-gradient-to-r from-purple-500 to-purple-600"
            trend="neutral"
            trendValue={0}
          />
          <StatCard
            title="إجمالي الباقات"
            value={stats.totalPackages}
            icon="📦"
            color="bg-gradient-to-r from-yellow-500 to-yellow-600"
            trend="up"
            trendValue={5}
          />
        </div>

        {/* Recent Activity & Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <span className="mr-2">📊</span>
                النشاط الأخير
              </h3>
            </div>
            <div className="p-6">
              {recentActivities.length > 0 ? (
                <div className="space-y-2">
                  {recentActivities.map((activity) => (
                    <RecentActivityItem
                      key={`${activity.type}-${activity.time}-${activity.title}`}
                      title={activity.title}
                      description={activity.description}
                      time={activity.time}
                      type={activity.type}
                      user={activity.user}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">📭</div>
                  <p className="text-gray-500 dark:text-gray-400">لا يوجد نشاط حديث</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <span className="mr-2">📈</span>
                إحصائيات سريعة
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex justify-between items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">نسبة المستخدمين النشطين</span>
                <span className="text-lg font-bold text-blue-900 dark:text-blue-100">
                  {stats.totalUsers > 0 ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">إجمالي الفئات</span>
                <span className="text-lg font-bold text-purple-900 dark:text-purple-100">
                  {stats.totalCategories}
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">إجمالي الباقات</span>
                <span className="text-lg font-bold text-yellow-900 dark:text-yellow-100">
                  {stats.totalPackages}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <span className="mr-2">⚡</span>
              إجراءات سريعة
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/admin/users')}
                className="group p-6 text-center border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">👥</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  إدارة المستخدمين
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.totalUsers} مستخدم
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/categories')}
                className="group p-6 text-center border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-purple-500 dark:hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">📁</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400">
                  إدارة الفئات
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.totalCategories} فئة
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/packages')}
                className="group p-6 text-center border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-yellow-500 dark:hover:border-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">📦</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-yellow-600 dark:group-hover:text-yellow-400">
                  إدارة الباقات
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {stats.totalPackages} باقة
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/account-approvals')}
                className="group p-6 text-center border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-green-500 dark:hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-300 transform hover:scale-105"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">📋</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400">
                  إدارة الموافقات
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  طلبات الموافقة
                </div>
              </button>
            </div>
          </div>
        </div>
    </div>
  );
});

export default DashboardPage;
